import React, { useState, useEffect } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { invoiceImportService } from '../services/invoiceImportService';
import { counterpartyService } from '../services/counterpartyService';
import { companyService } from '../services/companyService';
import { invoiceService } from '../services/invoiceService';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../hooks/useTranslation';
import { ParsedInvoice, ParsedBankAccount } from '../types/invoiceImport';
import { CreateCounterpartyRequest } from '../types/counterparty';
import { Currency } from '../types/company';
import { InvoiceStatus } from '../types/invoice';
import { CountrySelect } from './CountrySelect';

interface EnhancedInvoiceImportModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportStep = 'file_upload' | 'create_counterparty' | 'create_bank_account' | 'create_invoice';

interface CounterpartyFormData {
  name: string;
  taxId: string;
  vatId: string;
  address: string;
  country: string; // обязательное поле
  city: string; // обязательное поле
  zip: string; // обязательное поле
  contactPerson: string;
  email: string;
  phone: string;
}

interface BankAccountFormData {
  name: string;
  accountNumber: string;
  bankName: string;
  swift: string;
  iban: string;
  bankAddress: string;
  currency: Currency;
}

interface InvoiceFormData {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  saleDate: string;
  currency: Currency;
  notes: string;
}

interface EditableInvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

const EnhancedInvoiceImportModal: React.FC<EnhancedInvoiceImportModalProps> = ({
  companyId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  const [currentStep, setCurrentStep] = useState<ImportStep>('file_upload');
  const [parsedInvoice, setParsedInvoice] = useState<ParsedInvoice | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [createdCounterpartyId, setCreatedCounterpartyId] = useState<string | null>(null);
  const [createdBankAccountId, setCreatedBankAccountId] = useState<string | null>(null);
  const [editableItems, setEditableItems] = useState<EditableInvoiceItem[]>([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateInvoiceNumber, setDuplicateInvoiceNumber] = useState<string>('');
  
  // Формы для каждого шага
  const counterpartyForm = useForm<CounterpartyFormData>();
  const bankAccountForm = useForm<BankAccountFormData>();
  const invoiceForm = useForm<InvoiceFormData>();

  // Получаем информацию о компании для места выставления
  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companyService.getCompany(companyId),
    enabled: isOpen
  });

  // Функции для расчета сумм
  const calculateItemTotal = (item: EditableInvoiceItem) => {
    return item.quantity * item.unitPrice;
  };

  const calculateSubtotal = () => {
    return editableItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const calculateTaxAmount = () => {
    return editableItems.reduce((sum, item) => {
      const itemTotal = calculateItemTotal(item);
      const taxAmount = (itemTotal * item.taxRate) / 100;
      return sum + taxAmount;
    }, 0);
  };

  const calculateGrandTotal = () => {
    return calculateSubtotal() + calculateTaxAmount();
  };

  // Функция для добавления 14 дней к дате
  const addDaysToDate = (dateString: string, days: number): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  // Функции для обработки дубликатов
  const handleSaveWithNewNumber = () => {
    const currentNumber = invoiceForm.getValues('invoiceNumber');
    const newNumber = `${currentNumber}-${Date.now()}`;
    invoiceForm.setValue('invoiceNumber', newNumber);
    setShowDuplicateDialog(false);
    // Повторно запускаем создание инвойса
    createInvoiceMutation.mutate(invoiceForm.getValues());
  };

  const handleCancelImport = () => {
    setShowDuplicateDialog(false);
    handleClose();
  };

  const handleEditInvoiceNumber = () => {
    setShowDuplicateDialog(false);
    // Фокус на поле номера инвойса
    setTimeout(() => {
      const input = document.querySelector('input[name="invoiceNumber"]') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 100);
  };

  // Мутации для создания сущностей
  const createCounterpartyMutation = useMutation({
    mutationFn: (data: CreateCounterpartyRequest) => 
      counterpartyService.createCounterparty(companyId, data),
    onSuccess: async (response) => {
      setCreatedCounterpartyId(response.id);
      queryClient.invalidateQueries({ queryKey: ['counterparties', companyId] });
      
      // Проверяем, есть ли банковские счета для создания
      if (parsedInvoice?.bankAccounts && parsedInvoice.bankAccounts.length > 0) {
        const bankAccount = parsedInvoice.bankAccounts[0];
        try {
          // Проверяем, существует ли уже такой банковский счет
          const exists = await companyService.checkBankAccountExists(companyId, bankAccount.accountNumber);
          if (exists) {
            // Банковский счет уже существует, переходим к созданию инвойса
            setCurrentStep('create_invoice');
          } else {
            // Банковский счет не существует, создаем его
            setCurrentStep('create_bank_account');
          }
        } catch (error) {
          console.error('Error checking bank account:', error);
          // В случае ошибки переходим к созданию банковского счета
          setCurrentStep('create_bank_account');
        }
      } else {
        setCurrentStep('create_invoice');
      }
    },
    onError: (error) => {
      console.error('Error creating counterparty:', error);
      // Ошибка будет обработана через apiClient interceptor, который покажет toast-уведомление
      // Не показываем alert, чтобы избежать дублирования
    }
  });

  const createBankAccountMutation = useMutation({
    mutationFn: (data: BankAccountFormData) => 
      companyService.addBankAccount(companyId, {
        name: data.name || data.bankName, // Используем name, если указан, иначе bankName
        accountNumber: data.accountNumber,
        bankName: data.bankName,
        swift: data.swift,
        iban: data.iban,
        bankAddress: data.bankAddress,
        currency: data.currency,
        balance: 0
      }),
    onSuccess: (response) => {
      setCreatedBankAccountId(response.id);
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      setCurrentStep('create_invoice');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || t('companies.addAccountError');
      alert(errorMessage);
    },
    throwOnError: false // Предотвращаем проброс ошибки дальше в консоль
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async (data: InvoiceFormData) => {
      // Проверяем, существует ли инвойс с таким номером
      const exists = await invoiceService.checkInvoiceExists(companyId, data.invoiceNumber);
      if (exists) {
        setDuplicateInvoiceNumber(data.invoiceNumber);
        setShowDuplicateDialog(true);
        throw new Error('DUPLICATE_INVOICE');
      }

      return invoiceService.createInvoice(companyId, {
        invoiceNumber: data.invoiceNumber,
        clientId: createdCounterpartyId!,
        bankAccountId: createdBankAccountId || undefined,
        issueDate: data.issueDate,
        dueDate: data.dueDate,
        saleDate: data.saleDate,
        placeOfIssue: company?.city || 'Office',
        status: InvoiceStatus.DRAFT,
        currency: data.currency,
        notes: data.notes || '',
        items: editableItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate as any
        }))
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices', companyId] });
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      if (error.message === 'DUPLICATE_INVOICE') {
        // Диалог уже показан, ничего не делаем
        return;
      }
      console.error('Error creating invoice:', error);
      alert(t('invoices.createError'));
    }
  });

  // Загрузка файла и парсинг
  const handleFileUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    try {
      const result = await invoiceImportService.importInvoice(companyId, selectedFile);
      setParsedInvoice(result);
      
      // Определяем следующий шаг
      if (result.foundClientId) {
        // Контрагент найден, переходим к банковскому счету или инвойсу
        setCreatedCounterpartyId(result.foundClientId);
        
        // Проверяем, есть ли банковские счета для создания (счета нашей компании)
        if (result.bankAccounts && result.bankAccounts.length > 0) {
          const bankAccount = result.bankAccounts[0];
          try {
            // Проверяем, существует ли уже такой банковский счет
            const exists = await companyService.checkBankAccountExists(companyId, bankAccount.accountNumber);
            if (exists) {
              // Банковский счет уже существует, переходим к созданию инвойса
              setCurrentStep('create_invoice');
            } else {
              // Банковский счет не существует, создаем его
              setCurrentStep('create_bank_account');
            }
          } catch (error) {
            console.error('Error checking bank account:', error);
            // В случае ошибки переходим к созданию банковского счета
            setCurrentStep('create_bank_account');
          }
        } else {
          setCurrentStep('create_invoice');
        }
      } else {
        // Контрагент не найден, создаем его
        setCurrentStep('create_counterparty');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(t('invoices.importError'));
    } finally {
      setIsUploading(false);
    }
  };

  // Предзаполнение форм данными из парсинга
  useEffect(() => {
    if (parsedInvoice && currentStep === 'create_counterparty') {
      counterpartyForm.reset({
        name: parsedInvoice.buyerName || '',
        taxId: parsedInvoice.buyerTaxId || '',
        vatId: parsedInvoice.buyerVatId || '',
        address: parsedInvoice.buyerAddress || '',
        country: '',
        city: '',
        zip: '000',
        contactPerson: '',
        email: parsedInvoice.buyerEmail || '',
        phone: parsedInvoice.buyerPhone || ''
      });
    }
  }, [parsedInvoice, currentStep, counterpartyForm]);

  useEffect(() => {
    if (parsedInvoice && currentStep === 'create_bank_account' && parsedInvoice.bankAccounts.length > 0) {
      const bankAccount = parsedInvoice.bankAccounts[0];
      bankAccountForm.reset({
        name: bankAccount.bankName || '', // Используем bankName как название по умолчанию
        accountNumber: bankAccount.accountNumber || '',
        bankName: bankAccount.bankName || '',
        swift: bankAccount.swift || '',
        iban: bankAccount.iban || '',
        bankAddress: bankAccount.bankAddress || '',
        currency: (bankAccount.currency as Currency) || Currency.PLN
      });
    }
  }, [parsedInvoice, currentStep, bankAccountForm]);

  useEffect(() => {
    if (parsedInvoice && currentStep === 'create_invoice') {
      // Предзаполняем форму инвойса
      const issueDate = parsedInvoice.issueDate || '';
      const dueDate = parsedInvoice.dueDate || addDaysToDate(issueDate, 14); // Добавляем 14 дней если не указана дата оплаты
      
      invoiceForm.reset({
        invoiceNumber: parsedInvoice.invoiceNumber || '',
        issueDate: issueDate,
        dueDate: dueDate,
        saleDate: parsedInvoice.saleDate || issueDate,
        currency: parsedInvoice.currency || Currency.PLN,
        notes: parsedInvoice.notes || ''
      });

      // Инициализируем редактируемые элементы
      if (parsedInvoice.items && parsedInvoice.items.length > 0) {
        setEditableItems(parsedInvoice.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate
        })));
      }
    }
  }, [parsedInvoice, currentStep, invoiceForm]);

  const handleClose = () => {
    setCurrentStep('file_upload');
    setParsedInvoice(null);
    setSelectedFile(null);
    setCreatedCounterpartyId(null);
    setCreatedBankAccountId(null);
    setEditableItems([]);
    setShowDuplicateDialog(false);
    setDuplicateInvoiceNumber('');
    counterpartyForm.reset();
    bankAccountForm.reset();
    invoiceForm.reset();
    onClose();
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'file_upload':
        return t('invoices.import.title');
      case 'create_counterparty':
        return t('invoices.import.createCounterparty');
      case 'create_bank_account':
        return t('invoices.import.createBankAccount');
      case 'create_invoice':
        return t('invoices.import.createInvoice');
      default:
        return '';
    }
  };

  const renderStepIndicator = () => {
    const steps = ['file_upload', 'create_counterparty', 'create_bank_account', 'create_invoice'];
    const currentIndex = steps.indexOf(currentStep);
    
    return (
      <div className="flex items-center justify-center mb-6">
        {steps.map((step, index) => (
          <React.Fragment key={step}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              index <= currentIndex 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              {index + 1}
            </div>
            {index < steps.length - 1 && (
              <div className={`w-12 h-1 mx-2 ${
                index < currentIndex ? 'bg-blue-600' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{getStepTitle()}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {renderStepIndicator()}
          
          {/* Шаг 1: Загрузка файла */}
          {currentStep === 'file_upload' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.selectFile')}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">{t('invoices.chooseFile')}</span>
                      </p>
                      <p className="text-xs text-gray-500">PDF, DOC, DOCX</p>
                    </div>
                  </label>
                </div>
                {selectedFile && (
                  <div className="mt-2 text-sm text-green-600">
                    {t('invoices.import.fileSelected')}: {selectedFile.name}
                  </div>
                )}
                {!selectedFile && (
                  <div className="mt-2 text-sm text-gray-500">
                    {t('invoices.noFileChosen')}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={handleFileUpload}
                  disabled={!selectedFile || isUploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? t('common.loading') : t('invoices.parseFile')}
                </button>
              </div>
            </div>
          )}

          {/* Шаг 2: Создание контрагента */}
          {currentStep === 'create_counterparty' && (
            <form onSubmit={counterpartyForm.handleSubmit((data) => createCounterpartyMutation.mutate(data))} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('counterparty.name')} *
                  </label>
                  <input
                    type="text"
                    {...counterpartyForm.register('name', { required: t('counterparty.nameRequired') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {counterpartyForm.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600">{counterpartyForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('counterparty.taxId')}
                  </label>
                  <input
                    type="text"
                    {...counterpartyForm.register('taxId')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('counterparty.vatId')}
                  </label>
                  <input
                    type="text"
                    {...counterpartyForm.register('vatId')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('counterparty.email')}
                  </label>
                  <input
                    type="email"
                    {...counterpartyForm.register('email')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('counterparty.country')} *
                  </label>
                  <CountrySelect
                    value={counterpartyForm.watch('country') || ''}
                    onChange={(value) => counterpartyForm.setValue('country', value, { shouldValidate: true })}
                    placeholder={t('counterparty.countryPlaceholder') || 'Выберите страну...'}
                    required
                    error={counterpartyForm.formState.errors.country?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('counterparty.city')} *
                  </label>
                  <input
                    type="text"
                    {...counterpartyForm.register('city', { required: t('counterparty.cityRequired') || 'Город обязателен' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {counterpartyForm.formState.errors.city && (
                    <p className="mt-1 text-sm text-red-600">{counterpartyForm.formState.errors.city.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('counterparty.zip') || 'Почтовый индекс'} *
                  </label>
                  <input
                    type="text"
                    {...counterpartyForm.register('zip', { required: t('counterparty.zipRequired') || 'Почтовый индекс обязателен' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {counterpartyForm.formState.errors.zip && (
                    <p className="mt-1 text-sm text-red-600">{counterpartyForm.formState.errors.zip.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('counterparty.address')}
                </label>
                <textarea
                  {...counterpartyForm.register('address')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createCounterpartyMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createCounterpartyMutation.isPending ? t('common.saving') : t('counterparty.create')}
                </button>
              </div>
            </form>
          )}

          {/* Шаг 3: Создание банковского счета */}
          {currentStep === 'create_bank_account' && (
            <form onSubmit={bankAccountForm.handleSubmit((data) => createBankAccountMutation.mutate(data))} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companies.bankAccountName') || 'Название счета'} *
                  </label>
                  <input
                    type="text"
                    {...bankAccountForm.register('name', { required: t('validation.bankAccountNameRequired') || 'Название счета обязательно' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('companies.bankAccountNamePlaceholder') || 'Например: Основной счет'}
                  />
                  {bankAccountForm.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600">{bankAccountForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companies.accountNumber')} *
                  </label>
                  <input
                    type="text"
                    {...bankAccountForm.register('accountNumber', { required: t('validation.accountNumberRequired') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {bankAccountForm.formState.errors.accountNumber && (
                    <p className="mt-1 text-sm text-red-600">{bankAccountForm.formState.errors.accountNumber.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companies.bankName')} *
                  </label>
                  <input
                    type="text"
                    {...bankAccountForm.register('bankName', { required: t('validation.bankNameRequired') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {bankAccountForm.formState.errors.bankName && (
                    <p className="mt-1 text-sm text-red-600">{bankAccountForm.formState.errors.bankName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companies.swift')}
                  </label>
                  <input
                    type="text"
                    {...bankAccountForm.register('swift')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companies.iban')}
                  </label>
                  <input
                    type="text"
                    {...bankAccountForm.register('iban')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.bankAddress')}
                </label>
                <textarea
                  {...bankAccountForm.register('bankAddress')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setCurrentStep('create_invoice')}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.skip')}
                </button>
                <button
                  type="submit"
                  disabled={createBankAccountMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createBankAccountMutation.isPending ? t('common.saving') : t('companies.addAccount')}
                </button>
              </div>
            </form>
          )}

          {/* Шаг 4: Создание инвойса */}
          {currentStep === 'create_invoice' && (
            <form onSubmit={invoiceForm.handleSubmit((data) => createInvoiceMutation.mutate(data))} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.invoiceNumber')} *
                  </label>
                  <input
                    type="text"
                    {...invoiceForm.register('invoiceNumber', { required: t('validation.invoiceNumberRequired') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {invoiceForm.formState.errors.invoiceNumber && (
                    <p className="mt-1 text-sm text-red-600">{invoiceForm.formState.errors.invoiceNumber.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.currency')} *
                  </label>
                  <select
                    {...invoiceForm.register('currency', { required: t('validation.currencyRequired') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={Currency.PLN}>PLN</option>
                    <option value={Currency.EUR}>EUR</option>
                    <option value={Currency.USD}>USD</option>
                    <option value={Currency.GBP}>GBP</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.issueDate')} *
                  </label>
                  <input
                    type="date"
                    {...invoiceForm.register('issueDate', { required: t('validation.issueDateRequired') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.dueDate')}
                  </label>
                  <input
                    type="date"
                    {...invoiceForm.register('dueDate')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Дата реализации
                  </label>
                  <input
                    type="date"
                    {...invoiceForm.register('saleDate')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('invoices.notes')}
                </label>
                <textarea
                  {...invoiceForm.register('notes')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Редактируемая таблица позиций инвойса */}
              {editableItems.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-medium text-gray-900">{t('invoices.import.invoiceItems')}</h4>
                    <p className="text-sm text-gray-600">{t('invoices.import.editableItems')}</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('invoices.description')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            {t('invoices.quantity')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            {t('invoices.unitPrice')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            {t('invoices.taxRate')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            {t('invoices.total')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                            {t('common.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {editableItems.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => {
                                  const newItems = [...editableItems];
                                  newItems[index].description = e.target.value;
                                  setEditableItems(newItems);
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => {
                                  const newItems = [...editableItems];
                                  newItems[index].quantity = parseFloat(e.target.value) || 0;
                                  setEditableItems(newItems);
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unitPrice}
                                onChange={(e) => {
                                  const newItems = [...editableItems];
                                  newItems[index].unitPrice = parseFloat(e.target.value) || 0;
                                  setEditableItems(newItems);
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={item.taxRate}
                                onChange={(e) => {
                                  const newItems = [...editableItems];
                                  newItems[index].taxRate = parseFloat(e.target.value) || 0;
                                  setEditableItems(newItems);
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                              {calculateItemTotal(item).toFixed(2)} {invoiceForm.watch('currency') || 'USD'}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = editableItems.filter((_, i) => i !== index);
                                  setEditableItems(newItems);
                                }}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                {t('common.delete')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Кнопка добавления новой позиции */}
                  <button
                    type="button"
                    onClick={() => {
                      setEditableItems([...editableItems, {
                        description: '',
                        quantity: 1,
                        unitPrice: 0,
                        taxRate: 0
                      }]);
                    }}
                    className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('invoices.addItem')}
                  </button>

                  {/* Итоги */}
                  <div className="mt-6 bg-gray-50 rounded-lg p-4">
                    <h5 className="text-lg font-medium text-gray-900 mb-3">{t('invoices.import.calculatedTotal')}</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{t('invoices.subtotal')}:</span>
                        <span>{calculateSubtotal().toFixed(2)} {invoiceForm.watch('currency') || 'USD'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>{t('invoices.taxAmount')}:</span>
                        <span>{calculateTaxAmount().toFixed(2)} {invoiceForm.watch('currency') || 'USD'}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>{t('invoices.grandTotal')}:</span>
                        <span>{calculateGrandTotal().toFixed(2)} {invoiceForm.watch('currency') || 'USD'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createInvoiceMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createInvoiceMutation.isPending ? t('common.saving') : t('invoices.create')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Диалог дубликата инвойса */}
      {showDuplicateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('invoices.duplicateInvoice')}
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                {t('invoices.duplicateInvoiceMessage')}
              </p>
              <p className="text-sm font-medium text-gray-900 mt-2">
                Номер: {duplicateInvoiceNumber}
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleEditInvoiceNumber}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {t('invoices.editInvoiceNumber')}
              </button>
              <button
                onClick={handleSaveWithNewNumber}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {t('invoices.saveWithNewNumber')}
              </button>
              <button
                onClick={handleCancelImport}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('invoices.cancelImport')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedInvoiceImportModal;
